import { SKY_WORLD, SKY_WORLD_ASSETS } from '../../../packages/domain/src/86-sky-world-scale.mjs';

const ease = (value) => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
};

/** Select an actionable original source socket, in world metres. Reservoir
 * choice follows real lateral travel; a ruptured tank cannot remain the firing
 * objective. Collapse phases instead reveal the physically posed underside. */
export function skyCinderViewObjective(state) {
  const boss = state.boss;
  if (!boss?.cinderheart || !boss.authoredPose) return null;
  if (['locked', 'rolling', 'exposed', 'righting'].includes(boss.cinderheart.phase))
    return { kind: 'underside', point: boss.authoredPose.sockets.underside_core };
  const tanks = (boss.authoredProps ?? []).filter((p) => p.cinderheartTank);
  const ready = tanks.filter((p) => p.phase === 'ready');
  const prop = ready.reduce(
    (near, p) => (!near || Math.abs(p.x - state.x) < Math.abs(near.x - state.x) ? p : near),
    null,
  );
  return prop
    ? { kind: 'valve', point: prop.weakpoint, prop }
    : { kind: 'underside', point: boss.authoredPose.sockets.underside_core };
}

/** Composition alone blends between real sockets. Reservoir age supplies the
 *1s rupture/refill handoff; actual lateral travel supplies the valve choice.
 * The point returned here is not an aim target or collision surface. */
function compositionPoint(state) {
  const boss = state.boss,
    systems = boss.cinderheart;
  const tanks = boss.authoredProps.filter((p) => p.cinderheartTank).sort((a, b) => a.side - b.side);
  const core = boss.authoredPose.sockets.underside_core;
  if (tanks.length !== 2) return core;
  const availability = tanks.map((p) =>
    p.phase === 'ready' ? ease(p.age / 1) : p.phase === 'ruptured' ? 1 - ease(p.age / 1) : 0,
  );
  const travel = ease((state.x + 6) / 12);
  const left = availability[0] * (1 - availability[1] * travel);
  const right = availability[1] * (1 - availability[0] * (1 - travel));
  const collapse =
    systems.phase === 'locked'
      ? ease(systems.age / 1)
      : ['rolling', 'exposed', 'righting'].includes(systems.phase)
        ? 1
        : systems.phase === 'crawl' && systems.rolls > 0
          ? 1 - ease(systems.age / 1)
          : 0;
  return Object.fromEntries(
    ['x', 'y', 'z'].map((axis) => [
      axis,
      core[axis] +
        (1 - collapse) *
          (left * (tanks[0].weakpoint[axis] - core[axis]) +
            right * (tanks[1].weakpoint[axis] - core[axis])),
    ]),
  );
}

/** Balance pilot and source objective angularly without retreating or moving
 * the existing camera boom. The bounded lens may include the body centre, but
 * never fits an entire mountain-scale boss at the expense of the rider. This
 * projection owns no clock, aiming assistance, collision or physical state. */
export function skyCinderCameraFrame(base, state, aspect, pointFov) {
  const objective = skyCinderViewObjective(state);
  if (!objective || aspect >= 1) return base;
  const pilot = { x: state.x, y: state.y, z: state.pilotZ ?? SKY_WORLD.pilotZ };
  const sole = pilot.y + SKY_WORLD.pilotSoleOffset - SKY_WORLD.pilotAimHeight;
  const points = [
    { ...pilot, y: sole },
    { ...pilot, y: sole + SKY_WORLD_ASSETS.pilot.height },
    compositionPoint(state),
  ];
  const angles = points.map((p) => Math.atan2(p.x - base.x, base.z - p.z));
  const yaw = (Math.min(...angles) + Math.max(...angles)) / 2;
  const pitches = points.map((p) =>
    Math.atan2(p.y - base.y, Math.sin(yaw) * (p.x - base.x) + Math.cos(yaw) * (base.z - p.z)),
  );
  const pitch = (Math.min(...pitches) + Math.max(...pitches)) / 2;
  const frame = {
    ...base,
    lookX: base.x + Math.sin(yaw) * Math.cos(pitch),
    lookY: base.y + Math.sin(pitch),
    lookZ: base.z - Math.cos(yaw) * Math.cos(pitch),
  };
  const ordinate = (p) => {
    const forward = Math.sin(yaw) * (p.x - base.x) + Math.cos(yaw) * (base.z - p.z);
    const height = p.y - base.y;
    return (
      (height * Math.cos(pitch) - forward * Math.sin(pitch)) /
      (forward * Math.cos(pitch) + height * Math.sin(pitch))
    );
  };
  // Optional scenery context cannot shrink the actual rider below16% height.
  const pilotLimit =
    (Math.atan(Math.abs(ordinate(points[1]) - ordinate(points[0])) / 0.32) * 360) / Math.PI;
  const limit = Math.max(base.fov, Math.min(90, pilotLimit));
  for (const point of points)
    frame.fov = Math.max(frame.fov, Math.min(limit, pointFov(frame, point, aspect, 0.92)));
  const bounds = state.boss.authoredPose.bounds;
  const centre = Object.fromEntries(
    ['x', 'y', 'z'].map((a, i) => [a, (bounds.min[i] + bounds.max[i]) / 2]),
  );
  const contextFov = pointFov(frame, centre, aspect, 0.9);
  frame.fov = Math.max(frame.fov, Math.min(limit, contextFov));
  return frame;
}
